import React, { useState, useCallback, useRef, useMemo } from 'react';
import AssetHierarchyBrowser from './AssetHierarchyBrowser';
import AssetHierarchyTypeBrowser from './AssetHierarchyTypeBrowser';
import AssetHierarchyActionsContainer from './AssetHierarchyActionsContainer';
import AssetHierarchyDetailsContainer from './details/AssetHierarchyDetailsContainer';
import HierarchyMappingContainer from './actions/HierarchyMappingContainer';
import { autoScroll } from '../functions';
import { useMutation, useSubscription } from 'hooks/apollo';
import { useApolloClient } from '@apollo/client';
import io from '../io';
import { useToast } from 'hooks/toasts/useToast';

const initialState = { hierarchy: { id: null, name: null }, triggers: { viewDetails: false, mapping: false } };
const AssetHierarchyManagementContainer = () => {
  const addToast = useToast();
  const [state, setState] = useState(initialState);
  const refs = { detailsArea: useRef(null), mappingArea: useRef(null) };
  const client = useApolloClient();

  const triggerAction = useCallback(
    (hierarchy, trigger) => setState(prev => ({ hierarchy, triggers: { ...prev.triggers, [trigger]: true } })),
    []
  );
  const handleClose = useCallback(trigger => () => setState(prev => ({ ...prev, triggers: { ...prev.triggers, [trigger]: false } })), []);

  const mapWithAnotherHierarchy = hierarchy => {
    triggerAction(hierarchy, 'mapping');
    autoScroll(refs.mappingArea);
  };

  const synchronizeAssetEffect = hierarchy => {
    synchronizeAsset({ variables: { input: { hierarchyId: hierarchy.id } } });
  };

  const [synchronizeAsset] = useMutation(io.mutations.SYNCHRONIZE_ASSET);

  useSubscription(io.subscriptions.SYNCHRONIZE_ASSET_COMPLETED, {
    onData: data => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'assetHierarchies' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'assetHierarchiesType' });
      client.cache.gc();
      addToast(data.data.data.synchronizeAssetCompleted.message, 'success');
    }
  });

  useSubscription(io.subscriptions.SYNCHRONIZE_ASSET_FAILED, {
    onData: data => {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'assetHierarchies' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'assetHierarchiesType' });
      client.cache.gc();
      addToast(data.data.data.synchronizeAssetFailed.message, 'error');
    }
  });

  const hierarchyActions = [
    {
      label: 'AssetHierarchy.List.Actions.ViewDetails.Label',
      effect: hierarchy => {
        triggerAction(hierarchy, 'viewDetails');
        autoScroll(refs.detailsArea);
      }
    },
    {
      label: 'AssetHierarchy.List.Actions.MapWithAnother.Label',
      effect: mapWithAnotherHierarchy
    },
    {
      label: 'AssetHierarchy.List.Actions.SynchronizeAsset.Label',
      effect: synchronizeAssetEffect
    }
  ];

  const events = useMemo(
    () => ({
      onDisableHierarchy: () => false
    }),
    []
  );

  return (
    <>
      <AssetHierarchyBrowser hierarchyActions={hierarchyActions} events={events} />
      <div ref={refs.detailsArea} />
      {state.triggers.viewDetails && <AssetHierarchyDetailsContainer hierarchy={state.hierarchy} onClose={handleClose('viewDetails')} />}
      {state.triggers.mapping && <HierarchyMappingContainer hierarchy={state.hierarchy} />}
      <div ref={refs.mappingArea} />
      <AssetHierarchyTypeBrowser />
      <AssetHierarchyActionsContainer />
    </>
  );
};

export default AssetHierarchyManagementContainer;
